import React, { useState, useEffect, useCallback } from 'react';
import { saudiService } from '../../services/saudiService';
import PlotlyChart from '../../components/charts/PlotlyChart';
import LoadingSpinner from '../../components/shared/LoadingSpinner';

const fmt = (val) => {
  if (val === null || val === undefined) return '-';
  const abs = Math.abs(val);
  if (abs >= 1e9) return `${(val / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(val / 1e3).toFixed(1)}K`;
  return val.toFixed(2);
};

const CorrelationPage = ({ filterParams }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const res = await saudiService.getCorrelation(filterParams); setData(res.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [filterParams]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <LoadingSpinner message="Loading correlation analysis..." />;
  if (!data) return (
    <div className="text-center py-16 text-gray-500 dark:text-gray-400">
      <div className="text-4xl mb-3 opacity-30">&#x1f50d;</div>
      <p className="text-sm">No correlation data available.</p>
    </div>
  );

  // API: { scatter: [{date, net_flow, free_float_pct, free_float_change}], top_inflows: [...], top_outflows: [...], insight: "..." }
  const scatter = data.scatter || [];
  const topInflows = data.top_inflows || [];
  const topOutflows = data.top_outflows || [];
  const insight = data.insight || '';

  const xVals = scatter.map(s => s.net_flow);
  const yVals = scatter.map(s => s.free_float_change);
  const labels = scatter.map(s => s.date);
  const scatterColor = xVals.map(v => v >= 0 ? '#22c55e' : '#ef4444');

  return (
    <div className="space-y-4">
      {/* Scatter plot */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
        <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
          Foreign Net Flow vs Change in Foreign Free Float %
        </h4>
        <PlotlyChart
          interactive
          data={[{
            x: xVals, y: yVals, text: labels,
            type: 'scatter', mode: 'markers',
            marker: { color: scatterColor, size: 10, opacity: 0.8, line: { color: 'rgba(255,255,255,0.2)', width: 1 } },
            hovertemplate: 'Week: %{text}<br>Net Flow: %{customdata} SAR<br>FF% Change: %{y:.3f}pp<extra></extra>',
            customdata: xVals.map(v => fmt(v)),
          }]}
          layout={{
            xaxis: { title: 'Foreign Net Flow (SAR)', zeroline: true, zerolinecolor: '#666', zerolinewidth: 1, tickformat: '.2s' },
            yaxis: { title: 'Change in Foreign FF% (pp)', zeroline: true, zerolinecolor: '#666', zerolinewidth: 1 },
            shapes: [
              { type: 'line', x0: 0, x1: 0, y0: 0, y1: 1, yref: 'paper', line: { color: '#555', width: 1, dash: 'dot' } },
              { type: 'line', y0: 0, y1: 0, x0: 0, x1: 1, xref: 'paper', line: { color: '#555', width: 1, dash: 'dot' } },
            ],
          }}
          style={{ minHeight: 380 }}
        />
      </div>

      {/* Time series: net flow + FF% on dual axis */}
      {scatter.length > 0 && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg p-3">
          <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
            Foreign Net Flow & Free Float % Over Time
          </h4>
          <PlotlyChart
            data={[
              { x: labels, y: xVals, name: 'Net Flow (SAR)', type: 'bar',
                marker: { color: xVals.map(v => v >= 0 ? 'rgba(34,197,94,0.6)' : 'rgba(239,68,68,0.6)') },
                hovertemplate: '%{x}<br>Net Flow: %{customdata}<extra></extra>',
                customdata: xVals.map(v => fmt(v) + ' SAR') },
              { x: labels, y: scatter.map(s => s.free_float_pct), name: 'Foreign FF%', type: 'scatter', mode: 'lines+markers',
                line: { color: '#c9a96e', width: 2 }, marker: { size: 5 }, yaxis: 'y2',
                hovertemplate: '%{x}<br>FF%: %{y:.2f}%<extra></extra>' },
            ]}
            layout={{
              xaxis: { type: 'category', tickfont: { size: 10 } },
              yaxis: { title: '', tickformat: '.2s', tickfont: { size: 10 } },
              yaxis2: { overlaying: 'y', side: 'right', ticksuffix: '%', tickfont: { size: 10, color: '#c9a96e' }, showgrid: false },
              legend: { orientation: 'h', yanchor: 'bottom', y: 1.04, xanchor: 'left', x: 0 },
              shapes: [{ type: 'line', x0: 0, x1: 1, xref: 'paper', y0: 0, y1: 0, line: { color: '#666', width: 1, dash: 'dot' } }],
            }}
          />
        </div>
      )}

      {/* Insight */}
      {insight && (
        <div className="bg-[#c9a96e]/5 border border-[#c9a96e]/20 rounded-lg p-4">
          <h4 className="text-xs uppercase tracking-wider text-[#c9a96e] mb-2 font-medium">Auto-Generated Insight</h4>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{insight}</p>
        </div>
      )}

      {/* Top inflow/outflow tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">Top Inflow Weeks</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-[#0d1117]">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Week</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Net Flow</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">FF% Chg</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {topInflows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{r.date}</td>
                    <td className="px-3 py-1.5 text-right text-green-600 dark:text-green-400 font-medium tabular-nums">+{fmt(r.net_flow)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {r.free_float_change !== undefined ? `${r.free_float_change >= 0 ? '+' : ''}${r.free_float_change.toFixed(3)}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-500" />
            <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">Top Outflow Weeks</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-[#0d1117]">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Week</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">Net Flow</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500 dark:text-gray-400">FF% Chg</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {topOutflows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-1.5 text-gray-800 dark:text-gray-200">{r.date}</td>
                    <td className="px-3 py-1.5 text-right text-red-600 dark:text-red-400 font-medium tabular-nums">{fmt(r.net_flow)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {r.free_float_change !== undefined ? `${r.free_float_change >= 0 ? '+' : ''}${r.free_float_change.toFixed(3)}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorrelationPage;
